/**
 * CategoryIcon — one drawn 20px glyph per category and per sub-category (REDESIGN §7b).
 *
 * `stroke 1.75`, `currentColor`, optional soft hue circle behind it. Every key of the live
 * taxonomy resolves to a glyph (`categories.ts`); a transaction with no category gets the
 * neutral one — never a blank.
 *
 * The glyph table is a module constant, so the `dangerouslySetInnerHTML` below can never
 * receive user input; it keeps the table compact exactly as `web/src/components/Icon.tsx` does.
 */
import { categoryGlyph, categoryHue, groupGlyph, groupHue } from './categories.ts';
import type { CategoryHue } from './categories.ts';
import { glyphMarkup } from './glyphs.ts';
import type { GlyphName } from './glyphs.ts';
import './kit.css';

export interface CategoryIconProps {
  /** Financy `category.main`. */
  main?: string | null;
  /** Financy `category.sub`. */
  sub?: string | null;
  /** A personal display group key (`HOUSING`, `GROCERIES`, …) — wins over `main`/`sub`. */
  groupKey?: string | null;
  /** An explicit glyph — wins over everything, for the few places that are not a category. */
  glyph?: GlyphName;
  /** An explicit hue; otherwise derived from the category or group. */
  hue?: CategoryHue;
  /** Draw the category's hue as a soft background circle. */
  soft?: boolean;
  /** Glyph size in px (the circle, when `soft`, is 1.6× this). Default 20 per §7b. */
  size?: number;
  /** Accessible name — the category's Hebrew label. Omit next to a visible label. */
  title?: string;
  className?: string;
}

/** The soft circle is 1.6 glyphs wide; the glyph sits unscaled inside it. */
const SOFT_SCALE = 1.6;
const BOX = 24;
const SOFT_BOX = 38;
const SOFT_INSET = (SOFT_BOX - BOX) / 2;

export function CategoryIcon({
  main,
  sub,
  groupKey,
  glyph,
  hue,
  soft,
  size = 20,
  title,
  className,
}: CategoryIconProps) {
  const name: GlyphName =
    glyph ?? (typeof groupKey === 'string' && groupKey.length > 0 ? groupGlyph(groupKey) : categoryGlyph(main, sub));
  const tone: CategoryHue =
    hue ?? (typeof groupKey === 'string' && groupKey.length > 0 ? groupHue(groupKey) : categoryHue(main));
  const labelled = typeof title === 'string' && title.length > 0;
  const px = soft === true ? Math.round(size * SOFT_SCALE) : size;
  const markup = glyphMarkup(name);
  return (
    <svg
      className={`fk-cat fk-cat--${tone}${soft === true ? ' fk-cat--soft' : ''}${className ? ` ${className}` : ''}`}
      width={px}
      height={px}
      viewBox={soft === true ? `0 0 ${SOFT_BOX} ${SOFT_BOX}` : `0 0 ${BOX} ${BOX}`}
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
    >
      {labelled ? <title>{title}</title> : null}
      {soft === true ? <circle className="fk-cat__bg" cx={SOFT_BOX / 2} cy={SOFT_BOX / 2} r={SOFT_BOX / 2} /> : null}
      <g
        className="fk-cat__ink"
        transform={soft === true ? `translate(${SOFT_INSET} ${SOFT_INSET})` : undefined}
        dangerouslySetInnerHTML={{ __html: markup }}
      />
    </svg>
  );
}
